import { C, Btn, fmt, useIsMobile } from "./ui";
import { proximasCampanas } from "../services/calendarioSanitario";
import { imprimirReporte } from "../services/reporte";

const hoyISO = () => new Date().toISOString().slice(0, 10);
const money = (n) => "$ " + Math.round(n || 0).toLocaleString("es-UY");
const diasHasta = (iso) => Math.round((new Date(iso + "T12:00:00") - new Date().setHours(12, 0, 0, 0)) / 86400000);
const saludo = () => { const h = new Date().getHours(); return h < 12 ? "Buen día" : h < 20 ? "Buenas tardes" : "Buenas noches"; };
const FECHA_LARGA = () => new Date().toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" });

export function HomePanel({
  nombreCampo, ubicacion, potreros = [], transacciones = [], tareas = [], stockSanidad = [], insumos = [],
  pesadas = [], totalHa = 0, cargaGlobal = 0, clima, onNav,
}) {
  const isMobile = useIsMobile();
  const animales = potreros.flatMap(p => (p.animalesAsignados || []));
  const activos = animales.filter(a => (a.estado || "activo") === "activo");
  const totalAnim = activos.length;

  // ── Economía del mes ──
  const mes = hoyISO().slice(0, 7);
  const txMes = transacciones.filter(t => (t.fecha || "").slice(0, 7) === mes);
  const ing = txMes.filter(t => t.tipo === "ingreso").reduce((s, t) => s + (t.monto || 0), 0);
  const egr = txMes.filter(t => t.tipo === "egreso").reduce((s, t) => s + (t.monto || 0), 0);
  const resultado = ing - egr;

  // ── Alertas de stock ──
  const insumoBajo = insumos.filter(i => (i.stock_min || 0) > 0 && (i.cantidad || 0) < i.stock_min);
  const sanBajo = stockSanidad.filter(s => (s.stockMin || 0) > 0 && (s.cantidad || 0) < s.stockMin);
  const sanVenc = stockSanidad.filter(s => s.vencimiento && s.vencimiento < hoyISO());
  const insVenc = insumos.filter(i => i.vencimiento && i.vencimiento < hoyISO());
  const nAlertasStock = insumoBajo.length + sanBajo.length + sanVenc.length + insVenc.length;

  // ── "Requiere atención" (tareas + stock + calendario sanitario) ──
  const avisos = [];
  proximasCampanas(45).forEach(c => avisos.push({
    ic: "💉", sev: c.enDias <= 7 ? "d" : "w", titulo: c.nombre,
    meta: `Calendario sanitario · ${c.nota || ""}`.trim(),
    tag: "sanitario", chip: c.enDias === 0 ? "Hoy" : `${c.enDias}d`, orden: c.enDias,
  }));
  [...sanVenc, ...insVenc].forEach(s => avisos.push({
    ic: "⏰", sev: "d", titulo: `${s.nombre} — vencido`, meta: `Venció ${fmt(s.vencimiento)}`, tag: "stock", chip: "Vencido", orden: -1,
  }));
  [...insumoBajo, ...sanBajo].forEach(s => avisos.push({
    ic: "📦", sev: "w", titulo: `${s.nombre} — stock bajo`,
    meta: `${s.cantidad || 0} ${s.unidad || ""} · mín ${s.stock_min || s.stockMin}`, tag: "stock", chip: "Bajo mín.", orden: 5,
  }));
  tareas.filter(t => t.estado !== "hecha").forEach(t => {
    const d = diasHasta(t.fecha);
    if (d > 10) return;
    avisos.push({
      ic: "🗓️", sev: d < 0 ? "d" : d <= 2 ? "w" : "i",
      titulo: t.titulo || t.tipo, meta: `${t.tipo || "Tarea"} · ${fmt(t.fecha)}`, tag: "tarea",
      chip: d < 0 ? `Vencida ${-d}d` : d === 0 ? "Hoy" : d === 1 ? "Mañana" : `${d}d`, orden: d,
    });
  });
  avisos.sort((a, b) => a.orden - b.orden);
  const avisosTop = avisos.slice(0, 6);

  // ── Ganancia de peso (desde pesadas del campo) ──
  const porAnimal = {};
  pesadas.forEach(p => { if (p.kg && p.fecha) (porAnimal[p.animal_id] = porAnimal[p.animal_id] || []).push(p); });
  const gdps = [];
  Object.values(porAnimal).forEach(ps => {
    ps.sort((x, y) => x.fecha.localeCompare(y.fecha));
    if (ps.length >= 2) {
      const dias = (new Date(ps[ps.length - 1].fecha) - new Date(ps[0].fecha)) / 86400000;
      if (dias > 0) gdps.push((ps[ps.length - 1].kg - ps[0].kg) / dias);
    }
  });
  const gdpProm = gdps.length ? gdps.reduce((s, g) => s + g, 0) / gdps.length : null;

  // ── Composición del rodeo por categoría ──
  const comp = {};
  activos.forEach(a => { const k = a.categoria || "Sin categoría"; comp[k] = (comp[k] || 0) + 1; });
  const compArr = Object.entries(comp).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const compMax = compArr.length ? compArr[0][1] : 1;

  const sevColor = { d: C.danger, w: C.warn, i: C.disponible, o: C.ok };
  const sevBg = { d: "#F3DCDC", w: "#F6ECCB", i: "#DCE9F2", o: "#E1EFDC" };

  const QA = ({ ic, l, nav, primary }) => (
    <Btn size="sm" variant={primary ? "primary" : "secondary"} onClick={() => onNav?.(nav)}
      style={{ whiteSpace: "nowrap" }}>{ic} {l}</Btn>
  );
  const Card = ({ title, more, moreNav, children }) => (
    <div style={{ background: C.white, border: `1px solid ${C.arenaDark}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px 8px" }}>
        <div style={{ fontFamily: "serif", fontSize: 16, fontWeight: 600, color: C.tierra }}>{title}</div>
        {more && <span onClick={() => onNav?.(moreNav)} style={{ fontSize: 12, color: C.pasto, fontWeight: 600, cursor: "pointer" }}>{more} →</span>}
      </div>
      <div style={{ padding: "0 16px 16px" }}>{children}</div>
    </div>
  );
  const kpi = { background: C.white, border: `1px solid ${C.arenaDark}`, borderRadius: 14, padding: "14px 16px" };
  const kpiLbl = { fontFamily: "monospace", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.09em", color: C.mutedLight, fontWeight: 600, marginBottom: 8 };
  const kpiVal = { fontFamily: "serif", fontSize: 26, fontWeight: 600, lineHeight: 1, color: C.tierra };

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "16px 14px 90px" : "22px 24px", maxWidth: 1160, margin: "0 auto" }}>

        {/* Hero */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: "serif", fontSize: isMobile ? 22 : 26, fontWeight: 600, color: C.tierra }}>{saludo()}</div>
            <div style={{ fontSize: 13.5, color: C.muted, marginTop: 3, textTransform: "capitalize" }}>
              {FECHA_LARGA()} · {totalAnim} cabezas en {potreros.length} potreros
              {clima?.[0] && <span style={{ textTransform: "none" }}> · {clima[0].ic} {clima[0].max}°/{clima[0].min}°</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <QA ic="⚖️" l="Pesar" nav="rodeo" primary />
            <QA ic="💉" l="Tratamiento" nav="sanidad" />
            <QA ic="🔀" l="Mover" nav="rodeo" />
            <QA ic="＋" l="Tarea" nav="agenda" />
            <Btn size="sm" variant="secondary" style={{ whiteSpace: "nowrap" }}
              onClick={() => imprimirReporte({ nombreCampo, ubicacion, potreros, transacciones, totalHa, totalAnim, cargaGlobal })}>
              📄 Reporte
            </Btn>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12, marginBottom: 16 }}>
          <div style={kpi}>
            <div style={kpiLbl}>Hacienda activa</div>
            <div style={kpiVal}>{totalAnim} <span style={{ fontSize: 14, color: C.muted, fontFamily: "sans-serif" }}>cab.</span></div>
          </div>
          <div style={kpi}>
            <div style={kpiLbl}>Carga animal</div>
            <div style={kpiVal}>{Number(cargaGlobal || 0).toFixed(2)} <span style={{ fontSize: 14, color: C.muted, fontFamily: "sans-serif" }}>UG/ha</span></div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{Math.round(totalHa)} ha</div>
          </div>
          <div style={kpi}>
            <div style={kpiLbl}>Resultado del mes</div>
            <div style={{ ...kpiVal, color: resultado >= 0 ? C.ok : C.danger }}>{resultado >= 0 ? "+" : "−"}{money(Math.abs(resultado))}</div>
          </div>
          <div style={kpi}>
            <div style={kpiLbl}>Alertas de stock</div>
            <div style={{ ...kpiVal, color: nAlertasStock ? C.danger : C.ok }}>{nAlertasStock}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{nAlertasStock ? "requieren compra" : "todo en orden"}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.35fr 1fr", gap: 16 }}>
          {/* Requiere atención */}
          <Card title="Requiere atención" more="Ver agenda" moreNav="agenda">
            {avisosTop.length === 0 ? (
              <div style={{ fontSize: 13, color: C.muted, padding: "10px 0" }}>Todo al día. Sin vencimientos ni tareas urgentes. 👌</div>
            ) : avisosTop.map((a, i) => (
              <div key={i} onClick={() => {
                // Si es sanitario o tarea, ir a agenda con parámetro para crear tarea del aviso
                if (a.tag === "sanitario" || a.tag === "tarea") {
                  onNav?.("agenda", a);
                } else if (a.tag === "stock") {
                  onNav?.("insumos");
                }
              }}
                style={{ display: "flex", gap: 11, alignItems: "flex-start", padding: "10px 12px", borderRadius: 11, marginBottom: 8,
                  border: `1px solid ${C.arenaDark}`, background: C.fondo, cursor: "pointer", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: sevColor[a.sev] }} />
                <span style={{ fontSize: 17, marginLeft: 4 }}>{a.ic}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: C.tierra }}>
                    {a.titulo}
                    <span style={{ fontFamily: "monospace", fontSize: 10, color: C.mutedLight, border: `1px solid ${C.arenaDark}`, borderRadius: 5, padding: "0 5px", marginLeft: 6 }}>{a.tag}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>{a.meta}</div>
                </div>
                <span style={{ fontFamily: "monospace", fontSize: 10, fontWeight: 700, textTransform: "uppercase", padding: "2px 8px",
                  borderRadius: 20, whiteSpace: "nowrap", alignSelf: "center", color: sevColor[a.sev], background: sevBg[a.sev] }}>{a.chip}</span>
              </div>
            ))}
          </Card>

          {/* Columna derecha */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title="Economía del mes" more="Detalle" moreNav="economia">
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}><span>▲ Ingresos</span><span style={{ fontFamily: "monospace", fontWeight: 700, color: C.ok }}>{money(ing)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}><span>▼ Egresos</span><span style={{ fontFamily: "monospace", fontWeight: 700, color: C.danger }}>{money(egr)}</span></div>
                <div style={{ height: 9, borderRadius: 6, background: C.arena, overflow: "hidden", display: "flex" }}>
                  <i style={{ height: "100%", background: C.ok, width: `${ing + egr ? (ing / (ing + egr)) * 100 : 50}%` }} />
                  <i style={{ height: "100%", background: C.danger, width: `${ing + egr ? (egr / (ing + egr)) * 100 : 50}%` }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.arenaDark}`, paddingTop: 9, marginTop: 2 }}>
                  <b>Resultado neto</b><span style={{ fontFamily: "monospace", fontWeight: 700, color: resultado >= 0 ? C.ok : C.danger }}>{resultado >= 0 ? "+ " : "− "}{money(Math.abs(resultado))}</span>
                </div>
              </div>
            </Card>

            <Card title="Ganancia de peso" more="Rodeo" moreNav="rodeo">
              {gdpProm != null ? (
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <span style={{ fontFamily: "serif", fontSize: 28, fontWeight: 600, color: C.tierra }}>{gdpProm.toFixed(2)}</span>
                  <span style={{ color: C.muted, fontSize: 13 }}>kg/día prom.</span>
                  <span style={{ fontFamily: "monospace", fontSize: 11, color: C.muted, marginLeft: "auto" }}>{gdps.length} animal{gdps.length !== 1 ? "es" : ""}</span>
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: C.muted }}>Cargá al menos 2 pesajes por animal para ver la ganancia diaria.</div>
              )}
            </Card>

            <Card title="Composición del rodeo" more="Rodeo" moreNav="rodeo">
              {compArr.length === 0 ? (
                <div style={{ fontSize: 12.5, color: C.muted }}>Sin animales cargados aún.</div>
              ) : compArr.map(([cat, n]) => (
                <div key={cat} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 3 }}>
                    <span style={{ color: C.texto }}>{cat}</span><span style={{ fontFamily: "monospace", color: C.muted }}>{n}</span>
                  </div>
                  <div style={{ height: 7, borderRadius: 5, background: C.arena, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(n / compMax) * 100}%`, background: C.pastoLight }} />
                  </div>
                </div>
              ))}
            </Card>
          </div>
        </div>

        <div style={{ textAlign: "center", color: C.mutedLight, fontSize: 12, marginTop: 22 }}>
          {nombreCampo || "Tu campo"} · CampoIA
        </div>
      </div>
    </div>
  );
}
